import { useState } from 'react';
import { Button } from '../../components/Button';
import { copyToClipboard } from '../members/hooks';
import { downloadAuthFile } from '../../lib/authImage';
import type { QrImageFormat, QrImageParams } from './utils';

const SIZES = [512, 1024, 2048] as const;

export interface QrDownloadActionsProps {
  code: string;
  link: string;
  targetUrl?: string | null;
  isArt: boolean;
  /** Construye la ruta de la imagen para descargar (partner u operador). */
  imagePath: (params: QrImageParams) => string;
  orgId?: string;
  idPrefix: string;
}

/** Descargar PNG/SVG, copiar link y abrir destino (R14.2). El SVG no aplica a QR Art, que es una imagen. */
export function QrDownloadActions({ code, link, targetUrl, isArt, imagePath, orgId, idPrefix }: QrDownloadActionsProps) {
  const [size, setSize] = useState<number>(1024);
  const [busy, setBusy] = useState<QrImageFormat | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function download(format: QrImageFormat) {
    setBusy(format);
    setMessage(null);
    try {
      await downloadAuthFile(imagePath({ format, size, download: true }), `${code}${format === 'svg' ? '' : `-${size}`}.${format}`, { orgId });
    } catch {
      setMessage({ tone: 'error', text: 'No pudimos descargar el QR. Intenta de nuevo.' });
    } finally {
      setBusy(null);
    }
  }

  async function copy() {
    const ok = await copyToClipboard(link);
    setMessage(ok ? { tone: 'ok', text: `Copiamos ${link}` } : { tone: 'error', text: 'No pudimos copiar el link.' });
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`${idPrefix}-size`} className="flex items-center gap-2 text-xs font-medium text-ink-muted">
          Tamaño
          <select
            id={`${idPrefix}-size`}
            className="rounded-sm border border-line bg-white px-2 py-1.5 text-sm text-ink"
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
          >
            {SIZES.map((s) => (
              <option key={s} value={s}>{s} px</option>
            ))}
          </select>
        </label>
        <Button size="sm" loading={busy === 'png'} onClick={() => void download('png')}>Descargar PNG</Button>
        <Button
          size="sm"
          variant="secondary"
          loading={busy === 'svg'}
          disabled={isArt}
          title={isArt ? 'El QR Art es una imagen: el SVG está disponible solo para Clásico y Con estilo' : undefined}
          onClick={() => void download('svg')}
        >
          Descargar SVG
        </Button>
        <Button size="sm" variant="secondary" onClick={() => void copy()}>Copiar link</Button>
        {targetUrl && (
          <a href={targetUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-md border border-line bg-white px-3 py-1.5 text-sm font-semibold text-ink no-underline hover:bg-mist">
            Abrir destino
          </a>
        )}
      </div>
      {message && (
        <p role={message.tone === 'error' ? 'alert' : 'status'} className={`text-xs ${message.tone === 'error' ? 'text-error-text' : 'text-success-text'}`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
